import React, { useState, useEffect, useRef } from 'react';
import { Layout, Space, Typography, Button, Input, App, Modal, Badge, Tooltip } from 'antd';
import { CalculatorOutlined, SaveOutlined, ShareAltOutlined, DownloadOutlined, LoadingOutlined, HistoryOutlined, LogoutOutlined, BookOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

// Hub передаёт локаль через postMessage в embed-режиме — там нативный
// switcher не нужен. В standalone (прямой заход в браузере) даём
// пользователю явный выбор языка.
const IS_IN_IFRAME = typeof window !== 'undefined' && window.self !== window.top;
import type { CalculatorData } from './Calculator';
import type { SalesPlanData } from './SalesPlan';
import type { BudgetCategory } from './Budget';
import { saveResult, createShareLink, exportXlsx } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import History from './History';
import DocumentationModal, { useHasUnreadUpdates } from './DocumentationModal';

const NAV_ITEM_KEYS = [
  { id: 'calculator', titleKey: 'idea_calc.header.nav_calculator' },
  { id: 'competitor-analysis', titleKey: 'idea_calc.header.nav_competitors' },
  { id: 'sales-plan', titleKey: 'idea_calc.header.nav_sales_plan' },
  { id: 'forecast', titleKey: 'idea_calc.header.nav_forecast' },
  { id: 'summary', titleKey: 'idea_calc.header.nav_summary' },
  { id: 'notes', titleKey: 'idea_calc.header.nav_notes' },
];

// Nav with IntersectionObserver for smooth scroll tracking (no jitter)
const HeaderNav: React.FC = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState('calculator');
  const isClickScrolling = useRef(false);

  // Track sections with IntersectionObserver
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_ITEM_KEYS.forEach((item) => {
      const element = document.getElementById(item.id);
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleSections.set(item.id, entry.intersectionRatio);
            } else {
              visibleSections.delete(item.id);
            }

            // Don't update during click-triggered scroll
            if (isClickScrolling.current) return;

            // Find the most visible section
            let maxRatio = 0;
            let mostVisible = 'calculator';
            visibleSections.forEach((ratio, id) => {
              if (ratio > maxRatio) {
                maxRatio = ratio;
                mostVisible = id;
              }
            });

            if (visibleSections.size > 0) {
              setActiveId(mostVisible);
            }
          });
        },
        { threshold: [0.1, 0.5], rootMargin: '-80px 0px -50% 0px' }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const handleClick = (id: string) => {
    isClickScrolling.current = true;
    setActiveId(id);

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Allow observer updates again after scroll animation
    setTimeout(() => {
      isClickScrolling.current = false;
    }, 800);
  };

  return (
    <nav className="header-nav" style={{ display: 'flex', gap: 8, flex: 1, justifyContent: 'center' }}>
      {NAV_ITEM_KEYS.map((item) => (
        <button
          key={item.id}
          onClick={() => handleClick(item.id)}
          className={`header-nav-link ${activeId === item.id ? 'active' : ''}`}
        >
          {t(item.titleKey)}
        </button>
      ))}
    </nav>
  );
};

/** Safely convert to number — NaN / undefined / null → 0 */
function n(v: unknown): number {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}

function buildBackendPayload(calc?: CalculatorData, salesPlan?: SalesPlanData, budget?: BudgetCategory[], notes?: string): Record<string, unknown> {
  if (!calc) return {};
  return {
    parent_asin: calc.asin || '',
    purchase_price: n(calc.purchasePrice),
    shipping_price: n(calc.shippingPrice),
    shipping_cost_per_kg: n(calc.shippingCostPerKg),
    unit_weight_kg: n(calc.unitWeightKg),
    storage_fee: n(calc.storageFee),
    selling_price: n(calc.sellingPrice),
    referral_fee: n(calc.referralFee),
    fba_fee: n(calc.fbaFee),
    other_fees: n(calc.otherFees),
    ppc_cost: n(calc.ppcCost),
    cost_per_click: n(calc.ppcCost),
    conversion_rate: n(calc.conversionRate),
    ppc_sales_percentage: n(calc.ppcSalesPercentage),
    return_rate: n(calc.returnRate),
    resellable_returns: n(calc.resellableReturns),
    monthly_sales: n(calc.monthlySales),
    batch_quantity: n(calc.batchQuantity),
    storage_fee_per_unit: n(calc.storageFee),
    coupon_promo: 0,
    coupon_promo_enabled: calc.couponPromoEnabled ?? true,
    competitors: (calc.competitorAsins || []).map((a) => ({ asin: a, price: 0 })),
    sales_plan_data: salesPlan || null,
    budget_data: budget || null,
    notes: notes || '',
  };
}

export interface HeaderProps {
  calculatorData?: CalculatorData;
  salesPlanData?: SalesPlanData;
  budgetData?: BudgetCategory[];
  notesData?: string;
  onLoadResult?: (idOrShare: number | string) => void;
  loadedFileName?: string;
  loadedResultId?: number | null;
}

const Header: React.FC<HeaderProps> = ({ calculatorData, salesPlanData, budgetData, notesData, onLoadResult, loadedFileName, loadedResultId }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isAuthenticated, mode } = useAuthStore();
  const [calcName, setCalcName] = useState('');
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [_savedFileName, setSavedFileName] = useState(''); // Track last saved name
  const [historyOpen, setHistoryOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const hasUnreadDocs = useHasUnreadUpdates();


  // When a saved result is loaded, populate name fields
  React.useEffect(() => {
    if (loadedFileName) {
      setCalcName(loadedFileName);
      setSavedFileName(loadedFileName);
    }
  }, [loadedFileName]);

  const handleSave = async () => {
    const name = calcName.trim();
    if (!name) {
      message.warning(t('idea_calc.header.msg_enter_name'));
      return;
    }
    setSaving(true);
    try {
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      // Имя не менялось относительно загруженной записи → правим её (по id).
      // Сменили имя → resultId не шлём → сохранится копия в своё пространство.
      const resultId = loadedResultId != null && name === loadedFileName ? loadedResultId : undefined;
      const res = await saveResult(name, payload, resultId);
      if (res.success) {
        setSavedFileName(name);
        message.success(t('idea_calc.header.msg_saved'));
      } else {
        message.error(res.message || t('idea_calc.header.msg_save_error'));
      }
    } catch (e: unknown) {
      console.error('Save error:', e);
      message.error(t('idea_calc.header.msg_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      const blob = await exportXlsx(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calculator_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success(t('idea_calc.header.msg_file_downloaded'));
    } catch (e: any) {
      let detail = '';
      // Blob response type — need to read error body as text
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          detail = json.detail || text;
        } catch { detail = t('idea_calc.common.error'); }
      } else {
        detail = e?.response?.data?.detail || e?.message || '';
      }
      console.error('Export error:', detail, e);
      message.error(t('idea_calc.header.msg_export_error', { detail }));
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    const name = calcName.trim();
    if (!name) {
      message.warning(t('idea_calc.header.msg_save_first'));
      return;
    }
    setSharing(true);
    try {
      // Always save current data before sharing to ensure share link has latest data
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      const resultId = loadedResultId != null && name === loadedFileName ? loadedResultId : undefined;
      const saveRes = await saveResult(name, payload, resultId);
      if (!saveRes.success) {
        message.error(t('idea_calc.header.msg_save_before_share_failed'));
        return;
      }
      setSavedFileName(name);
      const res = await createShareLink(name);
      const shareId = res.share_id;
      const rawUrl = res.url || `/s/${shareId}`;
      let shareUrl: string;
      if (rawUrl.startsWith('http')) {
        shareUrl = rawUrl;
      } else {
        const base = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '');
        shareUrl = `${window.location.origin}${base}${rawUrl}`;
      }

      // In embed mode clipboard is blocked — show ID in modal
      if (mode === 'embed') {
        Modal.success({
          title: t('idea_calc.header.share_link_title'),
          content: (
            <div>
              <Typography.Text type="secondary">ID:</Typography.Text>
              <Input
                value={shareId}
                readOnly
                style={{ marginTop: 8 }}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary" copyable={{ text: shareUrl }}>
                  {shareUrl}
                </Typography.Text>
              </div>
            </div>
          ),
        });
      } else {
        try {
          await navigator.clipboard.writeText(shareUrl);
          message.success(t('idea_calc.header.msg_link_copied'));
        } catch {
          // Fallback if clipboard fails
          Modal.info({ title: t('idea_calc.header.share_link_label'), content: shareUrl });
        }
      }
    } catch (e: unknown) {
      console.error('Share error:', e);
      message.error(t('idea_calc.header.msg_share_error'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <Layout.Header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#001029',
        display: 'flex',
        alignItems: 'center',
        height: 64,
        padding: '0 24px',
        gap: 16,
      }}
    >
      <Space size={12} align="center" style={{ flexShrink: 0 }}>
        <CalculatorOutlined style={{ fontSize: 28, color: '#69b1ff' }} />
        <Typography.Title level={4} style={{ color: '#fff', margin: 0, whiteSpace: 'nowrap' }}>
          {t('idea_calc.header.logo_title')}
        </Typography.Title>
      </Space>
      <HeaderNav />
      <Space size={8} style={{ flexShrink: 0 }}>
        {!IS_IN_IFRAME && <LanguageSwitcher />}
        <Input
          size="small"
          placeholder={t('idea_calc.header.name_placeholder')}
          value={calcName}
          onChange={(e) => {
            const scrollY = window.scrollY;
            setCalcName(e.target.value);
            requestAnimationFrame(() => {
              window.scrollTo({ top: scrollY, behavior: 'instant' });
            });
          }}
          style={{ width: 140 }}
          onPressEnter={handleSave}
        />
        <Button
          size="small"
          type="primary"
          icon={saving ? <LoadingOutlined /> : <SaveOutlined />}
          onClick={handleSave}
          disabled={saving}
        >
          {t('idea_calc.header.save_button')}
        </Button>
        <Tooltip title={t('idea_calc.header.export_tooltip')}>
          <Button
            size="small"
            icon={exporting ? <LoadingOutlined /> : <DownloadOutlined />}
            onClick={handleExport}
            disabled={exporting}
            style={{ color: '#69b1ff', borderColor: '#69b1ff' }}
          />
        </Tooltip>
        <Tooltip title={t('idea_calc.header.share_tooltip')}>
          <Button
            size="small"
            icon={sharing ? <LoadingOutlined /> : <ShareAltOutlined />}
            onClick={handleShare}
            disabled={sharing}
            style={{ color: '#69b1ff', borderColor: '#69b1ff' }}
          />
        </Tooltip>
        <Tooltip title={t('idea_calc.header.docs_tooltip')}>
          <Badge dot={hasUnreadDocs} offset={[-2, 2]}>
            <Button
              size="small"
              icon={<BookOutlined />}
              onClick={() => setDocsOpen(true)}
              style={{ color: '#69b1ff', borderColor: '#69b1ff' }}
            />
          </Badge>
        </Tooltip>
        {isAuthenticated && (
          <Tooltip title={t('idea_calc.header.history_tooltip')}>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              style={{ color: '#69b1ff', borderColor: '#69b1ff' }}
            />
          </Tooltip>
        )}
        {mode === 'dashboard' && (
          <Tooltip title={t('idea_calc.header.logout_tooltip')}>
            <Button
              size="small"
              icon={<LogoutOutlined />}
              onClick={() => useAuthStore.getState().logoutDashboard()}
              style={{ color: '#ff7875', borderColor: '#ff7875' }}
            />
          </Tooltip>
        )}
      </Space>
      {isAuthenticated && onLoadResult && (
        <History open={historyOpen} onClose={() => setHistoryOpen(false)} onLoadResult={onLoadResult} />
      )}
      <DocumentationModal open={docsOpen} onClose={() => setDocsOpen(false)} />
    </Layout.Header>
  );
};

export default Header;
